import { Button, ButtonType } from '@resurs-internal/styx-react'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '../common/utils'
import { DocViewerModal, useDocViewer } from './DocViewerModal'
import styles from './Mock.module.scss'
import { useIsInternal } from './useIsInternal'
import { UseRealServiceButton } from './UseRealServiceButton'

const proxyPath = process.env.REACT_APP_RETAIL_FINANCE_MOCK_PROXY_PATH

const TERMINAL_STATUSES = ['COMPLETED', 'CANCELLED', 'FAILED']

const decodePdf = base64Pdf => {
  const binary = window.atob(base64Pdf.replace(/\s/g, ''))
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }
  return bytes
}

export const DigitalIdMockSigning = () => {
  const { token } = useParams()
  // The mock hands out the page URL with signer_id, so that is the name to read here.
  const { signer_id: signerId } = useQuery()
  const [session, setSession] = useState(null)
  const { previewDoc, openDoc, closeDoc } = useDocViewer()
  const isInternal = useIsInternal()

  useEffect(() => {
    fetch(`${proxyPath}/digital-id-mock/signing/${token}/session`, {
      headers: { Accept: 'application/json' },
    })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (data) setSession(data)
      })
      .catch(console.error)
  }, [token])

  const buildUrl = result => {
    let url = `${proxyPath}/digital-id-mock/signing/doSign?signingSessionId=${encodeURIComponent(token)}&result=${result}`
    if (signerId) url += `&signerId=${encodeURIComponent(signerId)}`
    return url
  }

  const isTerminal = TERMINAL_STATUSES.includes(session?.overallStatus)

  // Nothing redirects on our behalf once the browser lands here instead of the mock's own page, so
  // a finished session still needs a way back — the same URL doSign would have redirected to.
  const currentSigner =
    session?.signers?.find(s => s.signerId === signerId) ||
    session?.signers?.[0]
  const isBase64Pdf =
    session?.documentContentType?.toLowerCase() === 'base64_encoded_pdf'
  const documentName = session?.documentName || 'Signing document.pdf'
  const continueUrl =
    session?.overallStatus === 'COMPLETED'
      ? currentSigner?.onTaskCompletedUrl
      : currentSigner?.onTaskCanceledUrl

  return (
    <>
      <h1>Document Signing</h1>
      <p>Review the document below and sign to complete the process.</p>

      {session?.signers?.length > 0 && (
        <div className={styles.infoSection}>
          <div className={styles.sectionLabel}>Signers</div>
          {session.signers.map(s => (
            <div key={s.signerId} className={styles.listItem}>
              <span>{s.fullName || 'Unknown'}</span>
              {s.governmentId && (
                <span className={styles.meta}>{s.governmentId}</span>
              )}
              <span className={styles.badge}>{s.status}</span>
            </div>
          ))}
        </div>
      )}

      {session?.documentText && isBase64Pdf ? (
        <div className={styles.infoSection}>
          <div className={styles.sectionLabel}>Document</div>
          <div className={styles.listItem}>
            <span>{documentName}</span>
            <button
              type="button"
              className={styles.docLink}
              onClick={() =>
                openDoc({
                  doc: { source: documentName, contentType: 'application/pdf' },
                  pdfData: decodePdf(session.documentText),
                })
              }
            >
              Open
            </button>
          </div>
        </div>
      ) : session?.documentText ? (
        <div className={styles.infoSection}>
          <div className={styles.sectionLabel}>Document</div>
          <div className={styles.documentText}>{session.documentText}</div>
          {session.documentContentType && (
            <div className={styles.meta}>
              Content type: {session.documentContentType}
            </div>
          )}
        </div>
      ) : null}

      {isTerminal ? (
        <>
          <div className={styles.infoSection}>
            <div className={styles.sectionLabel}>Session status</div>
            <div className={styles.listItem}>
              <span className={styles.badge}>{session.overallStatus}</span>
            </div>
          </div>
          {continueUrl && (
            <div className={styles.lineWrapper}>
              <Button
                id="continueButtonId"
                fullWidth
                onClick={() => {
                  window.location.href = continueUrl
                }}
              >
                Continue
              </Button>
            </div>
          )}
        </>
      ) : (
        <>
          <div className={styles.lineWrapper}>
            <Button
              id="successButtonId"
              fullWidth
              onClick={() => {
                window.location.href = buildUrl('COMPLETE')
              }}
            >
              Sign Document
            </Button>
          </div>
          <div className={styles.lineWrapper}>
            <Button
              id="failureButtonId"
              type={ButtonType.Secondary}
              fullWidth
              onClick={() => {
                window.location.href = buildUrl('CANCEL')
              }}
            >
              Cancel
            </Button>
          </div>
        </>
      )}
      {isInternal && (
        <UseRealServiceButton
          apiPath={`${proxyPath}/digital_id_service/signing/${token}/use_real_service`}
        />
      )}
      {previewDoc && (
        <DocViewerModal
          doc={previewDoc.doc}
          url={previewDoc.url}
          pdfData={previewDoc.pdfData}
          onClose={closeDoc}
        />
      )}
    </>
  )
}
